import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/landing/Header";
import HowItWorks from "@/components/landing/HowItWorks";

const Footer = lazy(() => import("@/components/landing/Footer"));
const DownloadCTA = lazy(() => import("@/components/landing/DownloadCTA"));

const HowItWorksPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>How It Works – PieFolio AI</title>
        <meta
          name="description"
          content="See how PieFolio AI turns your holdings into a Health Score, an AI portfolio analysis, and a forward-looking dividend calendar in three steps."
        />
        <link rel="canonical" href="https://getpiefolio.com/how-it-works" />
        <link rel="alternate" type="text/markdown" href="https://getpiefolio.com/how-it-works.md" />
        <meta property="og:title" content="How PieFolio AI Works" />
        <meta property="og:url" content="https://getpiefolio.com/how-it-works" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to use PieFolio AI",
            step: [
              { "@type": "HowToStep", name: "Add your holdings", text: "Add holdings yourself. Free includes 1 portfolio with up to 25 holdings; there is no broker linking." },
              { "@type": "HowToStep", name: "Get your Health Score", text: "See a 0–100 Portfolio Health Score covering risk, diversification and income." },
              { "@type": "HowToStep", name: "Generate AI analysis", text: "Tap Generate for a plain-English report on your real positions." },
            ],
          })}
        </script>
      </Helmet>
      <Header />
      <main className="pt-24">
        <header className="container mx-auto px-6 lg:px-12 max-w-4xl text-center py-12">
          <h1 className="text-4xl lg:text-6xl font-extrabold tracking-tight mb-6">
            How <span className="text-gradient-sage">PieFolio AI</span> works
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground">
            Three steps from your first holding to an AI-written portfolio analysis.
          </p>
        </header>
        <section className="container mx-auto px-6 lg:px-12 max-w-3xl pb-8 text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            PieFolio AI is a portfolio tracker for everyday investors. It is not a broker: you never move money
            into the app. Instead, you add the shares, ETFs and funds you already hold across your brokerage
            accounts yourself, and PieFolio builds one clear picture of your portfolio. Free includes 1 portfolio
            with up to 25 holdings. We do not connect to brokers.
          </p>
          <p>
            From there, the app scores your portfolio from 0 to 100 on diversification, concentration risk,
            income quality and volatility. The AI analysis then explains in plain English what is driving that
            score — for example, whether one stock or one sector makes up too much of your money — and what you
            could look at next. The dividend calendar shows which payments are expected and when, so you can
            view the next 7 days on Free or up to 365 days with Premium. Free includes 3 AI portfolio analyses
            and 5 AI chat messages per month. AI Chat is live; Premium includes 300 chat messages per month and
            unlimited AI analysis under <a href="https://getpiefolio.com/terms#fair-use" className="text-primary hover:underline">fair use</a>.
          </p>
          <p>
            Shariah screening against AAOIFI standards and income purification calculations are on our
            long-term roadmap and are not currently available in the app. Everything is educational and is not
            investment advice; you stay in control of every decision.
          </p>
        </section>
        <HowItWorks />
        <Suspense fallback={null}>
          <DownloadCTA />
          <Footer />
        </Suspense>
      </main>
    </div>
  );
};

export default HowItWorksPage;
